import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageOutlined, SearchOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { BookOpen } from "lucide-react";
import { message } from "antd";

import SectionPage from "@/pages/SectionPage/SectionPage";
import ChatPage from "@/pages/ChatPage";
import ReferencePage from "@/pages/ReferencePage";
import type { GraphViewController } from "@/utility/graphViewStorage";

import Tabs from "@/components/Tabs";
import { useFontStyle } from "@/context/StyleContext";
import Logger from "@/utility/logger";
import { defaultChatMessage } from "@/lib/defaults";
import { parseMarkdownSections } from "@/utility/parseMarkdownSections";
import {
  DocumentReadinessNoticeData,
  getDocumentsNotReadyPayload,
  getDocumentsNotReadyNoticeFromDocuments,
  isDocumentsNotReadyError,
} from "@/utility/documentReadiness";
import { renderSectionContent } from "@/utility/renderSectionContent";
import {
  appendTaskUpdateIfNew,
  createActiveSectionTask,
  isRecoverableTaskInterruption,
  releaseSectionTaskOnFailure,
} from "@/utility/sectionTaskState";
import { createMissingSectionBookmarkError, isMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";
import { currentDocumentUploadName } from "@/utility/currentDocumentUpload";
import type { GroundingArtifact, GroundingReferenceTarget, ReferenceSectionOption } from "@/types/grounding";
import {
  extractGroundingArtifactFromResponseData,
  normalizeGroundingArtifactSources,
  parseGroundingReferenceTag,
  replaceGroundingMarkersForInsertion,
  stripLeadingAgentControlJson,
} from "@/utility/groundingMarkers";
import { queuedWordRun } from "@/hooks/useWordDocumentUtils/wordInsertion";

import axios from "axios";

interface Tab {
  id: string;
  title: string;
  description: string;
  content?: React.ReactNode;
  closable?: boolean;
  icon?: React.ReactNode;
}

const groundingArtifactHasMissingDocumentIds = (artifact?: GroundingArtifact | null) =>
  Boolean(artifact?.sources?.some((source) => source && !source.document_id));

const AGENT_STREAM_RECONNECT_DELAYS_MS = [2000, 5000, 10000];
const AGENT_FAILURE_STATUSES = ["failed", "error", "cancelled"];

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const getAgentStatusValue = (taskData: any) => String(taskData?.status || "").toLowerCase();

const isAgentFailureStatus = (status: string) => AGENT_FAILURE_STATUSES.includes(status);

const createAgenticTerminalError = (message: string) => {
  const error = new Error(message);
  (error as any).agenticTerminalFailure = true;
  return error;
};

const createAgenticReconnectExhaustedError = (message: string) => {
  const error = new Error(message);
  (error as any).agenticReconnectExhausted = true;
  return error;
};

const getAgentFailureMessage = (taskData: any, fallback = "Agent task failed") =>
  taskData?.error_message || taskData?.message || taskData?.error || fallback;

const getErrorDetailMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  const detailMessage =
    typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : "";

  return detailMessage.trim() || String(error?.message || "").trim();
};

const MainPage = ({
  downloadContextUnavailable,
  trackChanges,
  handleTrackChangesToggle,
  // insertTextAtSection,
  insertAtSection,
  hasSectionBookmarks,
  scrollToSection,
  scrollToGroundingReference,
  retrieveLLMOuputAsync,
  // createTable,
  getSelectedText,
  selectedSections,
  setSelectedSections,
  generatedSections,
  setGeneratedSections,
  sectionStatus,
  sections,
  templateTagsMap,
  selectedProject,
  selectedReportTemplate,
  sectionEvalStatus,
  setSectionEvalStatus,
  runTask,
  selectAllSections,
  setSelectAllSections,
  acceptChanges,
  declineChanges,
  outputDecision,
  setOutputDecision,
  sectionDialogContent,
  setSectionDialogContent,
  sourceDialogOpen,
  setSourceDialogOpen,
  sourceDialogContent,
  setSourceDialogContent,
  feedbackDialogOpen,
  setFeedbackDialogOpen,
  feedbackDialogContent,
  setFeedbackDialogContent,
  regenerateDialogOpen,
  setRegenerateDialogOpen,
  regenerateDialogContent,
  setRegenerateDialogContent,
  toggleRegenerateOptions,
  regenerateLoading,
  setRegenerateLoading,
  qualityCheckDialogOpen,
  setQualityCheckDialogOpen,
  qualityCheckDialogContent,
  setQualityCheckDialogContent,
  //formatDateToLongForm,
  bookmarks,
  projectDetailsExpanded,
  setProjectDetailsExpanded,
  projectPlaceholderDetails,
  messages,
  setMessages,
  messageChatId,
  setMessageChatId,
  /** Invoked when the chat tab is left; Home resets the thread id so the
   * next chat starts a fresh conversation (fresh-context chat). */
  onChatContextReset,
  selectedText,
  retrievellmOuputWOGroup,
  retrievellmOuputWOGroupAsync,
  retrieveSourceDocument,
  uploadedFiles,
  retreiveSourceDocumentMetadata,
  uploadDocument,
  deleteDocument,
  deleteTag,
  setShowError,
  titleMap,
  setTest,
  location,
  uploadedAgentFiles,
  setUploadedAgentFiles,
  transcribeAudio,
  synthesizeSpeech,
  retrieveAgentQuery,
  streamGetRequest,
  retrieveAgentStatus,
  retrieveAgentGrounding,
  retrieveChatHistory,
  retrieveChatAsyncTask,
  waitForChatAsyncResult,
  sectionGeneration,
  setSectionGeneration,
  fetchSettingsGroup,
  reportTemplateId,
  processDocument,
  retrieveDocumentsList,
  retrieveDocumentsForImageInsertion,
  retrieveDocumentImages,
  downloadDocumentImage,
  submitLangfuseFeedback,
  setSectionStatus,
  selectedReportTemplateId,
  logAuditAction,
  reportTemplateProjectId,
  getProjectChatHistory,
  getChatThread,
  setChatPinned,
  renameChatHistory,
  deleteChatHistory,
  startAgentQuery,
  getAgentTaskStatus,
  streamAgentTaskStatus,
  getModels,
  getProjectTags,
  getPromptTemplates,
}) => {
  const { draftStyles } = useFontStyle();
  const insertionLockRef = useRef(new Set<string>());
  const agenticRegenerationRequestIdsInFlightRef = useRef(new Set<string>());
  const groundingFetchesInFlightRef = useRef(new Map<string, Promise<GroundingArtifact | null>>());
  const groundingDocumentIdHydrationAttemptsRef = useRef(new Set<string>());
  const staleGroundingTaskIdsRef = useRef(new Set<string>());

  const [isAgentSectionRegenerationOn, setIsAgentSectionRegenerationOn] = useState(false);
  const [agenticRegenerationRequest, setAgenticRegenerationRequest] = useState<any>(null);
  const [documentReadinessNotice, setDocumentReadinessNotice] = useState<DocumentReadinessNoticeData | null>(null);
  const [groundingByTaskId, setGroundingByTaskId] = useState<Record<string, GroundingArtifact>>({});
  const [graphViewController, setGraphViewController] = useState<GraphViewController | null>(null);
  const [groundingLoadingTaskIds, setGroundingLoadingTaskIds] = useState<Record<string, boolean>>({});
  const [groundingErrorsByTaskId, setGroundingErrorsByTaskId] = useState<Record<string, string>>({});
  const [activeGroundingTarget, setActiveGroundingTarget] = useState<GroundingReferenceTarget | null>(null);
  const selectionDebounceRef = useRef<number | null>(null);
  const groundingContentControlHandlersRef = useRef<
    Array<{ remove: () => void; context: { sync: () => Promise<any> } }>
  >([]);

  useEffect(() => {
    setDocumentReadinessNotice(null);
    setGroundingByTaskId({});
    setGroundingLoadingTaskIds({});
    setGroundingErrorsByTaskId({});
    setActiveGroundingTarget(null);
    groundingFetchesInFlightRef.current.clear();
    groundingDocumentIdHydrationAttemptsRef.current.clear();
    staleGroundingTaskIdsRef.current.clear();
  }, [selectedProject?.id]);

  const clearDocumentReadinessNotice = () => setDocumentReadinessNotice(null);

  const tryClaimAgenticRegenerationRequest = useCallback((requestId?: string | null) => {
    if (!requestId) return false;

    if (agenticRegenerationRequestIdsInFlightRef.current.has(requestId)) {
      return false;
    }

    agenticRegenerationRequestIdsInFlightRef.current.add(requestId);
    return true;
  }, []);

  const releaseAgenticRegenerationRequest = useCallback((requestId?: string | null) => {
    if (!requestId) return;
    agenticRegenerationRequestIdsInFlightRef.current.delete(requestId);
  }, []);

  const cacheGroundingArtifact = useCallback((artifact?: GroundingArtifact | null, fallbackTaskId?: string | null) => {
    if (!artifact) return null;

    const taskId = artifact.task_id || fallbackTaskId;
    if (!taskId || staleGroundingTaskIdsRef.current.has(taskId)) return null;

    const cachedArtifact = normalizeGroundingArtifactSources({
      ...artifact,
      task_id: taskId,
    });

    setGroundingByTaskId((prev) => ({ ...prev, [taskId]: cachedArtifact }));
    setGroundingErrorsByTaskId((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setGroundingLoadingTaskIds((prev) => ({ ...prev, [taskId]: false }));

    return cachedArtifact;
  }, []);

  useEffect(() => {
    // Warm the grounding cache from frozen download snapshots WITHOUT the
    // selectedProject gate: in downloaded-report mode no project is set,
    // and gating here was the main reason a ✦ click stalled — the first
    // click fell through to a live /agent/tasks/:id/grounding fetch that
    // is slow or 404s for downloads older than the retention window.
    for (const section of generatedSections) {
      const artifact = section.snapshotGrounding as GroundingArtifact | null;
      const taskId = section.grounding_task_id;
      // Some frozen artifacts omit task_id (older snapshots); the snapshot
      // is keyed to the section's exact agent_task_id by the backend, so
      // accept it when the id matches OR is absent.
      if (!artifact || !taskId || (artifact.task_id && artifact.task_id !== taskId) || groundingByTaskId[taskId]) continue;
      groundingDocumentIdHydrationAttemptsRef.current.add(taskId);
      cacheGroundingArtifact(artifact, taskId);
    }
  }, [generatedSections, groundingByTaskId, cacheGroundingArtifact]);

  const fetchGroundingArtifact = useCallback(
    async (taskId?: string | null): Promise<GroundingArtifact | null> => {
      if (!taskId || !retrieveAgentGrounding) return null;

      if (staleGroundingTaskIdsRef.current.has(taskId)) {
        return null;
      }

      const cachedArtifact = groundingByTaskId[taskId];
      // Downloaded-report sections own a frozen snapshot — never re-fetch
      // that task live. A live fetch is what made ✦ clicks feel slow (the
      // grounding endpoint can take seconds) or empty (downloads outlive
      // the backend's grounding retention window); the snapshot IS the
      // source of truth for downloaded reports, exactly as printed.
      const ownsFrozenSnapshot = generatedSections.some(
        (section) => section.grounding_task_id === taskId && section.snapshotGrounding
      );
      if (ownsFrozenSnapshot) {
        // No cache entry means the snapshot was absent at hydration —
        // report the honest 404-style empty state instead of fetching.
        if (!cachedArtifact) {
          setGroundingErrorsByTaskId((prev) => ({ ...prev, [taskId]: "No grounding records are available for this task." }));
        }
        return cachedArtifact || null;
      }

      const shouldHydrateDocumentIds =
        cachedArtifact &&
        groundingArtifactHasMissingDocumentIds(cachedArtifact) &&
        !groundingDocumentIdHydrationAttemptsRef.current.has(taskId);

      if (cachedArtifact && !shouldHydrateDocumentIds) {
        return cachedArtifact;
      }

      const inFlight = groundingFetchesInFlightRef.current.get(taskId);
      if (inFlight) {
        return inFlight;
      }

      const request = (async () => {
        setGroundingLoadingTaskIds((prev) => ({ ...prev, [taskId]: true }));
        try {
          if (shouldHydrateDocumentIds) {
            groundingDocumentIdHydrationAttemptsRef.current.add(taskId);
          }

          const artifact = await retrieveAgentGrounding(taskId);
          if (staleGroundingTaskIdsRef.current.has(taskId)) {
            return null;
          }
          return cacheGroundingArtifact(artifact, taskId);
        } catch (error: any) {
          if (staleGroundingTaskIdsRef.current.has(taskId)) {
            return null;
          }

          if (cachedArtifact && shouldHydrateDocumentIds) {
            Logger.error("MainPage.tsx: Failed to hydrate grounding source document ids:", error, { taskId });
            return cachedArtifact;
          }

          const status = error?.response?.status;
          const errorMessage =
            status === 404 ? "No grounding records are available for this task." : "Failed to load grounding records.";
          setGroundingErrorsByTaskId((prev) => ({ ...prev, [taskId]: errorMessage }));
          if (status !== 404) {
            Logger.error("MainPage.tsx: Failed to fetch grounding artifact:", error, { taskId });
          }
          return null;
        } finally {
          groundingFetchesInFlightRef.current.delete(taskId);
          if (!staleGroundingTaskIdsRef.current.has(taskId)) {
            setGroundingLoadingTaskIds((prev) => ({ ...prev, [taskId]: false }));
          }
        }
      })();

      groundingFetchesInFlightRef.current.set(taskId, request);
      return request;
    },
    [cacheGroundingArtifact, generatedSections, groundingByTaskId, retrieveAgentGrounding]
  );

  const clearGroundingForSection = useCallback(
    (sectionOrId?: any) => {
      const sectionId = typeof sectionOrId === "string" ? sectionOrId : sectionOrId?.id;
      const existingSection =
        generatedSections.find((section) => section?.id === sectionId) ||
        (typeof sectionOrId === "object" ? sectionOrId : null);
      const taskId = existingSection?.grounding_task_id;

      if (!sectionId && !taskId) return;

      if (sectionId) {
        setGeneratedSections((prevSections) =>
          prevSections.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  grounding_task_id: null,
                  grounding_available: false,
                }
              : section
          )
        );
      }

      if (taskId) {
        staleGroundingTaskIdsRef.current.add(taskId);
        groundingFetchesInFlightRef.current.delete(taskId);
        setGroundingByTaskId((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        setGroundingLoadingTaskIds((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        setGroundingErrorsByTaskId((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        setActiveGroundingTarget((prev) => (prev?.taskId === taskId ? null : prev));
      }
    },
    [generatedSections, setGeneratedSections]
  );

  const generatedAgenticSections = useMemo(() => {
    return generatedSections.filter((section) => {
      const isAgentic = String(section?.type || "").toLowerCase() === "section_agentic";
      const isGenerated = Boolean(section?.content || outputDecision?.[section?.id]);
      return isAgentic && isGenerated && section?.id;
    });
  }, [generatedSections, outputDecision]);

  const generatedGroundingTaskIdsKey = useMemo(() => {
    return generatedAgenticSections
      .map((section) => section?.grounding_task_id)
      .filter(Boolean)
      .sort()
      .join(",");
  }, [generatedAgenticSections]);

  const generatedGroundingTaskIds = useMemo(() => {
    if (!generatedGroundingTaskIdsKey) return [];
    return generatedGroundingTaskIdsKey.split(",");
  }, [generatedGroundingTaskIdsKey]);

  useEffect(() => {
    if (!selectedProject?.id) return undefined;

    let cancelled = false;

    const loadExactGeneratedGrounding = async () => {
      for (const taskId of generatedGroundingTaskIds) {
        if (generatedAgenticSections.some((section) => section.grounding_task_id === taskId && section.snapshotGrounding?.task_id === taskId)) {
          continue;
        }
        const cachedArtifact = groundingByTaskId[taskId];
        const shouldHydrateDocumentIds =
          cachedArtifact &&
          groundingArtifactHasMissingDocumentIds(cachedArtifact) &&
          !groundingDocumentIdHydrationAttemptsRef.current.has(taskId);

        if (cancelled || (cachedArtifact && !shouldHydrateDocumentIds)) continue;
        const artifact = await fetchGroundingArtifact(taskId);
        if (cancelled || !artifact || staleGroundingTaskIdsRef.current.has(taskId)) continue;

        setGeneratedSections((prevSections) =>
          prevSections.map((section) =>
            section.grounding_task_id === taskId
              ? {
                  ...section,
                  grounding_available: true,
                  sources: artifact.sources || section.sources,
                }
              : section
          )
        );
      }
    };

    loadExactGeneratedGrounding();

    return () => {
      cancelled = true;
    };
  }, [fetchGroundingArtifact, generatedGroundingTaskIds, generatedAgenticSections, groundingByTaskId, selectedProject?.id]);

  const referenceSectionOptions = useMemo<ReferenceSectionOption[]>(() => {
    const generatedBySectionId = new Map<string, any>();
    generatedSections.forEach((generatedSection) => {
      if (generatedSection?.id) {
        generatedBySectionId.set(generatedSection.id, generatedSection);
      }
    });

    return (sections || [])
      .map((section) => {
        const generatedSection = generatedBySectionId.get(section.id);
        const isAgentic = String(section?.type || "").toLowerCase() === "section_agentic";
        const isGenerated = Boolean(generatedSection?.content || outputDecision?.[section.id]);
        const taskId = generatedSection?.grounding_task_id || null;
        const artifact = taskId ? groundingByTaskId[taskId] : null;
        const hasGroundingRecords = Boolean(
          artifact && ((artifact.sources || []).length > 0 || (artifact.rationales || []).length > 0)
        );

        let disabledReason = "";
        if (!isAgentic) {
          disabledReason = "Only agentic sections can have grounding references.";
        } else if (!isGenerated) {
          disabledReason = "Generate this agentic section to view references.";
        } else if (!taskId) {
          disabledReason = "No grounding task is linked to this generated section.";
        } else if (groundingLoadingTaskIds[taskId]) {
          disabledReason = "Grounding records are loading.";
        } else if (!hasGroundingRecords) {
          disabledReason = "No source or rationale records are available for this exact task.";
        }

        return {
          sectionId: section.id,
          label: section.display_name || section.name || "Untitled section",
          isAgentic,
          isGenerated,
          enabled: isAgentic && isGenerated && !!taskId && hasGroundingRecords,
          disabledReason,
          taskId,
        };
      })
      .filter((option) => option.enabled)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }, [generatedSections, groundingByTaskId, groundingLoadingTaskIds, outputDecision, sections]);

  const activateGroundingTarget = useCallback(
    (target: GroundingReferenceTarget) => {
      if (downloadContextUnavailable) {
        message.warning("Frozen grounding is unavailable for this downloaded report.");
        return;
      }
      setActiveGroundingTarget({ ...target, activationKey: Date.now() });
      setActiveTabId("references");
      fetchGroundingArtifact(target.taskId);
    },
    [downloadContextUnavailable, fetchGroundingArtifact]
  );

  const inspectSelectedGroundingMarker = useCallback(async () => {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      const parentControl = selection.parentContentControlOrNullObject;
      parentControl.load("isNullObject, tag");
      await context.sync();

      if (parentControl.isNullObject) {
        return;
      }

      const target = parseGroundingReferenceTag(parentControl.tag);
      if (!target) {
        return;
      }

      activateGroundingTarget(target);
    });
  }, [activateGroundingTarget]);

  useEffect(() => {
    if (typeof Office === "undefined" || !Office.context?.document?.addHandlerAsync) return undefined;

    const handleSelectionChanged = () => {
      if (selectionDebounceRef.current) {
        window.clearTimeout(selectionDebounceRef.current);
      }

      selectionDebounceRef.current = window.setTimeout(async () => {
        try {
          await inspectSelectedGroundingMarker();
        } catch (error) {
          Logger.warn("MainPage.tsx: Unable to inspect selected Word reference marker:", error);
        }
      }, 150);
    };

    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      handleSelectionChanged,
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          Logger.warn("MainPage.tsx: Failed to register document selection handler:", result.error);
        }
      }
    );

    return () => {
      if (selectionDebounceRef.current) {
        window.clearTimeout(selectionDebounceRef.current);
      }

      Office.context.document.removeHandlerAsync?.(
        Office.EventType.DocumentSelectionChanged,
        { handler: handleSelectionChanged } as any
      );
    };
  }, [inspectSelectedGroundingMarker]);

  useEffect(() => {
    if (typeof Word === "undefined") return undefined;
    let cancelled = false;

    const clearGroundingContentControlHandlers = async () => {
      const handlers = groundingContentControlHandlersRef.current;
      groundingContentControlHandlersRef.current = [];

      for (const handlerResult of handlers) {
        try {
          handlerResult.remove();
          await handlerResult.context.sync();
        } catch (error) {
          Logger.warn("MainPage.tsx: Failed to remove grounding content-control handler:", error);
        }
      }
    };

    const handleGroundingContentControlEvent = async (event: Word.ContentControlEnteredEventArgs) => {
      const controlId = event.ids?.[0];
      if (!controlId) return;

      try {
        await queuedWordRun(async (context) => {
          const control = context.document.contentControls.getByIdOrNullObject(controlId);
          control.load("isNullObject, tag");
          await context.sync();

          if (control.isNullObject) {
            return;
          }

          const target = parseGroundingReferenceTag(control.tag);
          if (!target) {
            return;
          }

          activateGroundingTarget(target);
        });
      } catch (error) {
        Logger.warn("MainPage.tsx: Unable to inspect clicked grounding reference marker:", error);
      }
    };

    const registerGroundingContentControlHandlers = async () => {
      try {
        await queuedWordRun(async (context) => {
          if (cancelled) return;
          await clearGroundingContentControlHandlers();
          if (cancelled) return;

          const contentControls = context.document.contentControls;
          contentControls.load("items/id,items/tag");
          await context.sync();

          if (cancelled) return;

          contentControls.items.forEach((control) => {
            if (!parseGroundingReferenceTag(control.tag)) return;

            groundingContentControlHandlersRef.current.push(control.onEntered.add(handleGroundingContentControlEvent));
            groundingContentControlHandlersRef.current.push(
              control.onSelectionChanged.add(handleGroundingContentControlEvent)
            );
          });

          await context.sync();
        });
      } catch (error) {
        Logger.warn("MainPage.tsx: Failed to register grounding content-control handlers:", error);
      }
    };

    registerGroundingContentControlHandlers();

    return () => {
      cancelled = true;
      void clearGroundingContentControlHandlers();
    };
  }, [activateGroundingTarget, generatedGroundingTaskIdsKey]);

  const createDocumentsNotReadyError = (documents: any[] = []) => {
    const readinessNotice = getDocumentsNotReadyNoticeFromDocuments(documents);
    const message =
      readinessNotice?.message ||
      "The request cannot be completed because project documents are not ready. Please wait for processing to finish or retry failed documents.";
    const error = new Error(message);

    (error as any).response = {
      data: {
        error: "documents_not_ready",
        message,
      },
    };

    return error;
  };

  const hasDocumentInList = (documents: any[], documentId?: string) =>
    !documentId || documents.some((document) => document?.id === documentId || document?.document_id === documentId);

  const waitForProjectDocumentsReady = async (projectId: string, uploadedDocumentId?: string) => {
    const timeoutMs = 5 * 60 * 1000;
    const pollIntervalMs = 2000;
    const startedAt = Date.now();
    let latestDocuments: any[] = [];

    Logger.debug("MainPage.tsx: Starting project documents readiness poll...");

    while (Date.now() - startedAt <= timeoutMs) {
      const documentsResponse = await retrieveDocumentsList({ projectId, perPage: 100 });
      latestDocuments = documentsResponse?.items || [];

      const readinessNotice = getDocumentsNotReadyNoticeFromDocuments(latestDocuments);
      const hasUploadedDocument = hasDocumentInList(latestDocuments, uploadedDocumentId);

      if (!readinessNotice && hasUploadedDocument) {
        Logger.debug("MainPage.tsx: Project documents are ready.");
        return;
      }

      const hasFailedDocument = latestDocuments.some(
        (document) => String(document?.state || "").toUpperCase() === "FAILED"
      );

      if (hasFailedDocument) {
        throw createDocumentsNotReadyError(latestDocuments);
      }

      Logger.debug("MainPage.tsx: Project documents are not ready yet. Polling again...");

      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs >= timeoutMs) break;

      await new Promise((resolve) => setTimeout(resolve, Math.min(pollIntervalMs, timeoutMs - elapsedMs)));
    }

    const includesUploadedDocument = hasDocumentInList(latestDocuments, uploadedDocumentId);
    const documentsForError = includesUploadedDocument
      ? latestDocuments
      : [...latestDocuments, { id: uploadedDocumentId, state: "processing" }];

    throw createDocumentsNotReadyError(documentsForError);
  };

  // Handles the sending of messages in the chat
  const handleSendMessage = async (
    message: string,
    options?: {
      llm_name?: string;
      include_tag_ids?: string[];
      exclude_tag_ids?: string[];
    }
  ): Promise<{ answer: string; id?: string; sources?: any[] }> => {
    if (!selectedProject?.id) {
      throw new Error("Please select a project first");
    }

    const reportTemplateId =
      selectedReportTemplateId && selectedReportTemplateId !== "default"
        ? selectedReportTemplateId
        : undefined;

    const response = await retrievellmOuputWOGroup(
      selectedProject.id,
      messageChatId,
      message,
      null,
      "chat",
      reportTemplateId,
      // Add-in chat page (Lite mode): opt in to the interactive
      // surface so the resulting chat_history row shows up in the
      // chat with documents page's history list.
      "interactive_chat",
      options
    );
    if (response?.id) setMessageChatId(response.id);

    return response;
  };

  // Explicit New Chat action: resets conversation messages and thread id
  const handleNewChat = useCallback(() => {
    setMessages(defaultChatMessage);
    onChatContextReset?.();
  }, [onChatContextReset, setMessages]);

  const [tabs, setTabs] = useState<Tab[]>([
    {
      id: "home",
      title: "Generation",
      description: "Generate document sections",
      icon: <ThunderboltOutlined />,
      closable: false,
    },
    {
      id: "chat",
      title: "Chat",
      description: "Question and Answer with AI",
      icon: <MessageOutlined />,
      closable: false,
    },
    {
      id: "references",
      title: "Sources",
      description: "Review generated output",
      icon: <BookOpen size={14} />,
      closable: false,
    },
  ]);

  const [activeTabId, setActiveTabId] = useState<string>(tabs[0]?.id || "");

  // Chat memory: leaving the Chat tab preserves the conversation so returning
  // users can continue their chat. Only an explicit 'New Chat' action resets it.
  const prevActiveTabIdRef = useRef<string>(activeTabId);
  useEffect(() => {
    prevActiveTabIdRef.current = activeTabId;
  }, [activeTabId]);

  const scrollToItemTop = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Scrolls to the last section added to generatedSections
  const scrollToLastGenerated = () => {
    const lastId = generatedSections[generatedSections.length - 1]?.id;
    setTimeout(() => {
      scrollToItemTop(lastId);
    }, 500);
  };

  const handleCurrentDocxUpload = async (project_id: string, uploadFilename?: string) => {
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 65536 }, function (result) {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const file = result.value;
          const sliceCount = file.sliceCount;

          const docdata: Uint8Array[] = new Array(sliceCount);

          const getSlice = (index: number) => {
            file.getSliceAsync(index, async function (sliceResult) {
              if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                docdata[index] = new Uint8Array(sliceResult.value.data);

                if (index + 1 < sliceCount) {
                  getSlice(index + 1);
                } else {
                  file.closeAsync();

                  const fileBlob = new Blob(docdata as any, {
                    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });

                  // Named by the section that asked for it, so the two
                  // snapshots stay distinguishable in the documents list.
                  const filename = uploadFilename || "document.docx";

                  const fileAsFile = new File([fileBlob], filename, {
                    type: fileBlob.type,
                  });

                  let uploadedFileInfo: { documentId: string; tags: any[] } | null = null;

                  try {
                    const res = await uploadDocument(project_id, [], fileAsFile);

                    if (res?.id && res?.tags) {
                      uploadedFileInfo = {
                        documentId: res.id,
                        tags: res.tags,
                      };

                      Logger.debug("MainPage.tsx: Uploaded document information:", res?.id);

                      await processDocument(res.id);

                      await waitForProjectDocumentsReady(project_id, res.id);

                      resolve(uploadedFileInfo);
                    } else {
                      throw new Error("The current Word document upload did not return a document ID and temporary tag.");
                    }
                  } catch (error) {
                    Logger.error("MainPage.tsx: Fetching errors after uploading document", error);
                    if (uploadedFileInfo) {
                      try {
                        await deleteDocument(uploadedFileInfo.documentId);

                        const tagsToDelete = uploadedFileInfo.tags.filter(
                          (tag) => tag.name && tag.name.startsWith("tmp-")
                        );
                        await Promise.all(tagsToDelete.map((tag) => deleteTag(tag.id)));
                      } catch (cleanupError) {
                        Logger.error(
                          "MainPage.tsx: Failed to clean up temporary document after upload error:",
                          cleanupError
                        );
                      }
                    }

                    reject(error);
                  }
                }
              } else {
                Logger.error("MainPage.tsx: getSliceAsync error:", sliceResult.error.message);
                file.closeAsync();
                reject(new Error(sliceResult.error.message));
              }
            });
          };

          getSlice(0);
        } else {
          Logger.error("MainPage.tsx: getFileAsync failed:", result.error.message);
          reject(new Error(result.error.message));
        }
      });
    });
  };

  const getAgentTaskData = (payload: any) => {
    if (
      payload?.data &&
      (payload.data.response_data || payload.data.status || payload.data.answer || payload.data.trace_url)
    ) {
      return payload.data;
    }
    return payload;
  };

  const getTraceUrl = (taskData: any) =>
    typeof taskData?.trace_url === "string" && taskData.trace_url.trim().length > 0
      ? taskData.trace_url
      : null;

  const applyAgentTaskDataToResponse = useCallback(
    (taskData: any, taskId: string, llmResponse: any) => {
      if (!taskData) return null;

      const responseData = taskData.response_data || taskData.result;
      const answer = responseData?.answer || taskData.answer || taskData.result?.answer;

      if (answer) {
        llmResponse.answer = answer;
        llmResponse.sources = responseData?.sources || taskData.sources || llmResponse.sources;
        llmResponse.id = responseData?.id ?? responseData?.chat_history_id ?? taskData.id ?? llmResponse.id;
        llmResponse.grounding_task_id = taskId;
      }

      const groundingArtifact = extractGroundingArtifactFromResponseData(responseData, taskId);
      if (groundingArtifact && !staleGroundingTaskIdsRef.current.has(taskId)) {
        llmResponse.groundingArtifact = cacheGroundingArtifact(groundingArtifact, taskId) || groundingArtifact;
        llmResponse.grounding_task_id = llmResponse.groundingArtifact.task_id || taskId;
      }

      const traceUrl = getTraceUrl(taskData);
      if (traceUrl) {
        llmResponse.trace_url = traceUrl;
      }

      return taskData;
    },
    [cacheGroundingArtifact]
  );

  const markAgenticSectionFailed = useCallback(
    (section: any, errorMessage: string, taskId?: string | null) => {
      setGeneratedSections((prevSections) =>
        prevSections.map((generatedSection) =>
          generatedSection.id === section.id
            ? releaseSectionTaskOnFailure(generatedSection, errorMessage, taskId)
            : generatedSection
        )
      );

      setOutputDecision((prev) => ({
        ...prev,
        [section.id]: true,
      }));
    },
    [setGeneratedSections, setOutputDecision]
  );

  const handleAgentStream = async (query, section, llmResponse) => {
    let latestTaskData: any = null;
    let latestRecoverabilityErrorMessage = "";

    const appendAgentUpdate = (update: any) => {
      setGeneratedSections((prevSections) =>
        prevSections.map((s) => {
          if (s.id !== section.id) return s;

          return {
            ...s,
            agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
              ...update,
              task_id: update?.task_id || query.task_id,
            }),
          };
        })
      );
    };

    const readLatestTaskData = async () => {
      const latest = await retrieveAgentStatus(query.task_id);
      return getAgentTaskData(latest);
    };

    const applyTerminalTaskData = (taskData: any) => {
      const latestStatus = getAgentStatusValue(taskData);

      if (isAgentFailureStatus(latestStatus)) {
        throw createAgenticTerminalError(getAgentFailureMessage(taskData));
      }

      if (latestStatus !== "completed") {
        return false;
      }

      if (taskData?.response_data || taskData?.answer || taskData?.result) {
        applyAgentTaskDataToResponse(taskData, query.task_id, llmResponse);
      }

      llmResponse.trace_url = getTraceUrl(taskData) || llmResponse.trace_url || null;

      if (!llmResponse.answer && !llmResponse.groundingArtifact?.markdown) {
        throw createAgenticTerminalError("Agent task completed without response data");
      }

      appendAgentUpdate({
        message: "Task complete.",
        status: "completed",
      });

      return true;
    };

    for (let attempt = 0; attempt <= AGENT_STREAM_RECONNECT_DELAYS_MS.length; attempt += 1) {
      let terminalTaskData: any = null;

      try {
        await new Promise<void>((resolve, reject) => {
          streamGetRequest(
            query.task_id,
            // calls the onMessage handler: it runs automatically every time the server pushes a new SSE message
            (data) => {
              const taskData = getAgentTaskData(data);
              const status = getAgentStatusValue(taskData);
              const streamedTraceUrl = getTraceUrl(taskData);
              Logger.debug("MainPage.tsx: Received task update:", taskData?.status);

              if (streamedTraceUrl) {
                llmResponse.trace_url = streamedTraceUrl;
              }

              if (taskData?.response_data || taskData?.answer || taskData?.result) {
                applyAgentTaskDataToResponse(taskData, query.task_id, llmResponse);
                Logger.debug("MainPage.tsx: Agent response received");
                Logger.debug("MainPage.tsx: Agent response_data:", taskData.response_data);
              }

              if (status === "completed" || isAgentFailureStatus(status)) {
                terminalTaskData = taskData;
              }

              appendAgentUpdate(typeof data === "object" ? data : { message: data, status: "running" });
            },
            // calls the onError handler: it runs if the stream connection breaks or fails
            (error) => {
              Logger.error("MainPage.tsx: Stream error:", error);
              reject(error);
            },
            // calls the onClose handler: it runs when the server successfully closes the stream
            () => {
              Logger.debug("MainPage.tsx: Stream closed.");
              resolve();
            }
          );
        });

        if (terminalTaskData && isAgentFailureStatus(getAgentStatusValue(terminalTaskData))) {
          applyTerminalTaskData(terminalTaskData);
        }

        const hasUsableTerminalData =
          terminalTaskData &&
          getAgentStatusValue(terminalTaskData) === "completed" &&
          Boolean(llmResponse.answer || llmResponse.groundingArtifact?.markdown);

        if (hasUsableTerminalData) {
          latestTaskData = terminalTaskData;

          if (!llmResponse.trace_url) {
            try {
              latestTaskData = await readLatestTaskData();
            } catch (statusError) {
              Logger.warn("MainPage.tsx: Agent completed, but latest status fetch failed.", statusError);
            }
          }
        } else {
          latestTaskData = await readLatestTaskData();
        }

        if (applyTerminalTaskData(latestTaskData)) {
          return;
        }

      } catch (error: any) {
        if (error?.agenticTerminalFailure) {
          throw error;
        }

        latestRecoverabilityErrorMessage = getErrorDetailMessage(error) || latestRecoverabilityErrorMessage;
        Logger.warn("MainPage.tsx: Agent stream interrupted. Checking task status before reconnecting.", error);

        try {
          latestTaskData = await readLatestTaskData();
          if (applyTerminalTaskData(latestTaskData)) {
            return;
          }
        } catch (statusError: any) {
          if (statusError?.agenticTerminalFailure) {
            throw statusError;
          }
          latestRecoverabilityErrorMessage =
            getErrorDetailMessage(statusError) || latestRecoverabilityErrorMessage;
          Logger.warn("MainPage.tsx: Agent status check failed during reconnect.", statusError);
        }
      }

      if (attempt >= AGENT_STREAM_RECONNECT_DELAYS_MS.length) {
        break;
      }

      appendAgentUpdate({
        message: "Connection interrupted. Reconnecting...",
        status: "running",
      });
      await wait(AGENT_STREAM_RECONNECT_DELAYS_MS[attempt]);
    }

    throw createAgenticReconnectExhaustedError(
      latestRecoverabilityErrorMessage || "Agent connection could not be restored. Please try again."
    );
  };

  const appendChatAsyncUpdate = (section, data) => {
    const status = data?.status || "running";
    const message =
      data?.message || (status?.toLowerCase() === "completed" ? "Task complete." : data?.chat_task_status || status);
    if (!message) return;

    const newUpdate = {
      message,
      status,
      task_id: data?.task_id,
      chat_task_status: data?.chat_task_status,
      event_seq: data?.event_seq,
      updated_at: data?.updated_at,
    };

    setGeneratedSections((prevSections) =>
      prevSections.map((s) => {
        if (s.id !== section.id) return s;

        const updates = s.agentUpdates || [];
        const lastUpdate = updates[updates.length - 1];
        if (
          (newUpdate.event_seq && lastUpdate?.event_seq === newUpdate.event_seq) ||
          (!newUpdate.event_seq && lastUpdate?.message === newUpdate.message && lastUpdate?.status === newUpdate.status)
        ) {
          return s;
        }

        const nextUpdates = appendTaskUpdateIfNew(updates, newUpdate);
        if (nextUpdates === updates) {
          return s;
        }

        return {
          ...s,
          agentUpdates: nextUpdates,
          chat_async_task_id: data?.task_id || s.chat_async_task_id,
          activeSectionTask:
            createActiveSectionTask(
              "chat_async",
              s.activeSectionTask?.mode || (s.chatAsyncRegenerationInProgress ? "regeneration" : "generation"),
              data?.task_id || s.activeSectionTask?.taskId || s.chat_async_task_id
            ) || s.activeSectionTask,
        };
      })
    );
  };

  const upsertGeneratedSection = (section) => {
    setGeneratedSections((prevSections) => {
      const existingIndex = prevSections.findIndex((s) => s.id === section.id);
      if (existingIndex === -1) return [...prevSections, section];

      return prevSections.map((s) =>
        s.id === section.id
          ? {
              ...s,
              ...section,
              agentUpdates: section.agentUpdates || s.agentUpdates,
            }
          : s
      );
    });
  };

  const handleAIOutput = async (llmResponse, section, isLocked = false) => {
    if (llmResponse && llmResponse.answer) {
      let output = llmResponse.answer;
      const isAgenticSection = section.type.toLowerCase() === "section_agentic";
      const groundingTaskId = isAgenticSection
        ? llmResponse.grounding_task_id || llmResponse.groundingArtifact?.task_id || section.activeSectionTask?.taskId || section.task_id
        : null;
      const groundingArtifact: GroundingArtifact | null = isAgenticSection
        ? llmResponse.groundingArtifact || (groundingTaskId ? groundingByTaskId[groundingTaskId] || null : null)
        : null;

      if (groundingArtifact?.markdown) {
        output = replaceGroundingMarkersForInsertion(groundingArtifact.markdown, groundingArtifact.citation_numbers);
      } else if (isAgenticSection) {
        output = replaceGroundingMarkersForInsertion(stripLeadingAgentControlJson(output));
      }

      section.chat_history_id = llmResponse.id || null;
      section.trace_url =
        typeof llmResponse?.trace_url === "string" && llmResponse.trace_url.trim().length > 0
          ? llmResponse.trace_url
          : null;

      const sources = llmResponse.sources || [];
      const tagged_document_ids = llmResponse.tagged_document_ids || [];
      for (const docId of tagged_document_ids) {
        const doc = await retreiveSourceDocumentMetadata(docId);
        if (doc) {
          sources.push({
            document: doc.filename,
            file_id: doc.id,
            project_id: selectedProject.id,
            content: "",
          });
        }
      }

      section.sources = sources;
      section.content = output;
      section.grounding_task_id = groundingTaskId || null;
      section.grounding_available = !!groundingArtifact;
      if (section.fromReportDownload) section.snapshotGrounding = groundingArtifact;

      let stateUpdaterForInsert;
      if (isAgenticSection) {
        // Update state manually for agentic
        setGeneratedSections((prevSections) =>
          prevSections.map((s) => {
            if (s.id === section.id) {
              const updates = s.agentUpdates || [];
              return {
                ...s,
                content: output,
                sources: sources,
                chat_history_id: llmResponse.id || null,
                trace_url: section.trace_url,
                grounding_task_id: groundingTaskId || null,
                grounding_available: !!groundingArtifact,
                snapshotGrounding: section.fromReportDownload ? groundingArtifact : s.snapshotGrounding,
                activeSectionTask: undefined,
                task_id: null,
                agenticInProgress: false,
                agenticRegenerationInProgress: false,
                agentUpdates: appendTaskUpdateIfNew(updates, {
                  message: "Task complete.",
                  status: "completed",
                  task_id: groundingTaskId || s.task_id,
                }),
              };
            }
            return s;
          })
        );
        // Don't let insertAtSection update state
        stateUpdaterForInsert = () => {};
      } else {
        // Update state manually for chat-async
        setGeneratedSections((prevSections) =>
          prevSections.map((s) => {
            if (s.id !== section.id) return s;
            return {
              ...s,
              content: output,
              sources: sources,
              chat_history_id: llmResponse.id || null,
              trace_url: section.trace_url,
              chatAsyncInProgress: false,
              chatAsyncRegenerationInProgress: false,
              activeSectionTask: undefined,
              evalStatus: undefined,
            };
          })
        );
        // Don't let insertAtSection update state
        stateUpdaterForInsert = () => {};
      }

      const key = `${section.insertion_key}`;
      const needsLocking = !isLocked;
      if (needsLocking) {
        if (insertionLockRef.current.has(key)) {
          Logger.warn(`MainPage.tsx: Skipping insertAtSection for key ${key} because it is already locked.`);
          return;
        }
        insertionLockRef.current.add(key);
      }
      try {
        await renderSectionContent({
          markdown: groundingArtifact?.markdown || llmResponse.answer,
          section, grounding: groundingArtifact, groundingTaskId,
          insert: section.fromReportDownload ? undefined : insertAtSection,
          projectId: selectedProject?.id, draftStyles,
          retrieveDocumentsForImageInsertion, retrieveDocumentImages, downloadDocumentImage,
          setGeneratedSections: stateUpdaterForInsert, setSectionStatus,
        });

        setOutputDecision((prev) => ({
          ...prev,
          [section.id]: true,
        }));

      } finally {
        if (needsLocking) {
          insertionLockRef.current.delete(key);
        }
      }
    }
  };

  const fetchWithRetry = async (fn, retries = 3, delay = 2000) => {
    try {
      return await fn();
    } catch (error) {
      if (isDocumentsNotReadyError(error)) {
        throw error;
      }

      if (retries <= 0) throw error;

      Logger.warn(`MainPage.tsx: Network request failed. Retrying in ${delay}ms... (${retries} attempts left)`);

      await new Promise((resolve) => setTimeout(resolve, delay));

      return fetchWithRetry(fn, retries - 1, delay * 2);
    }
  };

  const handleSectionGeneration = async (hasChatCommandActivated?: boolean) => {
    try {
      if (sectionGeneration === true) return;

      Logger.debug("MainPage.tsx: Selected sections count:", selectedSections?.length);

      if (
        (!selectedSections && hasChatCommandActivated === false) ||
        (selectedSections.length === 0 && hasChatCommandActivated === false)
      )
        return;

      Logger.trace("MainPage.tsx: Section generation started", {
        action: "section_generation",
        sectionCount: selectedSections?.length,
        sectionNames: selectedSections?.map((s) => s.display_name),
      });
      clearDocumentReadinessNotice();
      setSectionGeneration(true);

      const generationPromises = async (section) => {
        let uploadedFileInfo: { documentId: string; tags: any[] } | null = null;
        let createdGeneratedSectionPlaceholder = false;
        let generationStage = "init";
        let activeTaskId: string | null = null;
        let activeTaskKind: "chat_async" | "agentic" | null = null;
        let activeTaskMode: "generation" | "regeneration" = "generation";

        try {
          Logger.debug("MainPage.tsx: Processing section:", section.name);

          generationStage = "bookmark-preflight";
          const sectionBookmarksExist = await hasSectionBookmarks(section.insertion_key);
          if (!sectionBookmarksExist) {
            throw createMissingSectionBookmarkError(section.display_name);
          }

          const currentDocumentName = currentDocumentUploadName(section.display_name);
          const includeCurrentDocument = currentDocumentName !== null;

          let exec_tag_ids = [];

          if (currentDocumentName) {
            try {
              generationStage = "document-upload";
              const results: any = await handleCurrentDocxUpload(selectedProject.id, currentDocumentName);
              Logger.debug(`MainPage.tsx: Document upload results for ${section.display_name} section received`);
              if (results && results.documentId) {
                uploadedFileInfo = results;
                results.tags.forEach((item) => exec_tag_ids.push(item.id));
              }
            } catch (error) {
              Logger.error(`MainPage.tsx: Error uploading document for ${section.display_name} section:`, error);
              throw error;
            }
          }

          let message = section.user_query;

          let llmResponse: any = {};
          const handleChatAsyncProgress = (event) => {
            activeTaskId = event?.task_id || activeTaskId;
            activeTaskKind = "chat_async";
            appendChatAsyncUpdate(section, event);
          };

          if (section.type.toLowerCase() === "section_agentic") {
            const initialSectionData = {
              ...section,
              content: "",
              sources: [],
              trace_url: null,
              agentUpdates: [{ message: "Initializing agent...", status: "pending" }],
              chat_history_id: null,
              activeSectionTask: undefined,
              agenticInProgress: true,
              agenticRegenerationInProgress: false,
            };
            setGeneratedSections((prevSections) => [...prevSections, initialSectionData]);
            createdGeneratedSectionPlaceholder = true;

            generationStage = "agent-query";
            const query = await fetchWithRetry(() =>
              retrieveAgentQuery(selectedProject.id, section.id, uploadedFileInfo?.documentId)
            );
            activeTaskId = query.task_id;
            activeTaskKind = "agentic";

            // Store task_id on section so reconnection can use it without re-triggering a new execution
            setGeneratedSections((prevSections) =>
              prevSections.map((s) =>
                s.id === section.id
                  ? {
                      ...s,
                      trace_url: null,
                      task_id: query.task_id,
                      activeSectionTask: createActiveSectionTask("agentic", "generation", query.task_id),
                      agenticRegenerationInProgress: false,
                    }
                  : s
              )
            );

            generationStage = "agent-stream";
            await handleAgentStream(query, section, llmResponse);
          } else {
            const initialSectionData = {
              ...section,
              content: "",
              sources: [],
              trace_url: null,
              agentUpdates: [{ message: "Submitting section generation request...", status: "running" }],
              chatAsyncInProgress: true,
              chatAsyncRegenerationInProgress: false,
              chat_async_task_id: null,
              chat_history_id: null,
              activeSectionTask: undefined,
            };
            upsertGeneratedSection(initialSectionData);
            createdGeneratedSectionPlaceholder = true;
            generationStage = "llm-request";
            if (!includeCurrentDocument) {
              llmResponse = await fetchWithRetry(() =>
                retrieveLLMOuputAsync(selectedProject.id, section.id, handleChatAsyncProgress)
              );
            } else {
              llmResponse = await fetchWithRetry(() =>
                retrievellmOuputWOGroupAsync(
                  selectedProject.id,
                  messageChatId,
                  message,
                  exec_tag_ids,
                  section.name,
                  undefined,
                  handleChatAsyncProgress,
                  // Section generation flow: stamp the row as
                  // non-interactive so the chat_history row never
                  // shows up on the chat with documents page.
                  "non_interactive_chat"
                )
              );
            }
          }

          let isLockedForInsert = false;
          const key = `${section.insertion_key}`;
          try {
            if (isAgentSectionRegenerationOn) {
              generationStage = "decline-previous-changes";
              if (insertionLockRef.current.has(key)) {
                Logger.warn(`MainPage.tsx: Skipping regeneration for key ${key} because it is already locked.`);
                return;
              }
              insertionLockRef.current.add(key);
              isLockedForInsert = true;
              await declineChanges(section.insertion_key);
            }

            generationStage = "word-insertion";
            if (!isLockedForInsert) {
              if (insertionLockRef.current.has(key)) {
                Logger.warn(`MainPage.tsx: Skipping insertion for key ${key} because it is already locked.`);
                return;
              }
              insertionLockRef.current.add(key);
              isLockedForInsert = true;
            }
            await handleAIOutput(llmResponse, section, true);

            // Audit trail: log CREATE or UPDATE action for section generation
            if (reportTemplateProjectId) {
              logAuditAction(
                reportTemplateProjectId,
                isAgentSectionRegenerationOn ? "UPDATE" : "CREATE",
                section.display_name
              );
            }

            setSelectedSections((prev) => prev.filter((s) => s.id !== section.id));
          } finally {
            if (isLockedForInsert) {
              insertionLockRef.current.delete(key);
              isLockedForInsert = false;
            }
          }
        } catch (error: any) {
          Logger.error(`MainPage.tsx: Section "${section.display_name}" failed at stage "${generationStage}":`, error, {
            stage: generationStage,
            sectionId: section.id,
            sectionType: section.type,
            status: error?.response?.status,
          });

          if (isMissingSectionBookmarkError(error)) {
            message.warning(error.message);
            setSectionStatus((prev) => ({ ...prev, [section.id]: false }));
            setGeneratedSections((prevSections) =>
              prevSections.map((generatedSection) =>
                generatedSection.id === section.id
                  ? {
                      ...generatedSection,
                      activeSectionTask: undefined,
                      chatAsyncInProgress: false,
                      chatAsyncRegenerationInProgress: false,
                      agenticInProgress: false,
                      agenticRegenerationInProgress: false,
                    }
                  : generatedSection
              )
            );
            return;
          }

          if (
            activeTaskKind === "agentic" &&
            (generationStage === "agent-stream" || error?.agenticTerminalFailure || error?.agenticReconnectExhausted)
          ) {
            const responseDetail = error.response?.data?.detail?.trim?.() || error.message?.trim?.() || "";
            const errorMessage = responseDetail || "Agentic section generation failed. Please try again.";
            markAgenticSectionFailed(section, errorMessage, activeTaskId);
            return;
          }

          const activeSectionTask = createActiveSectionTask(activeTaskKind as any, activeTaskMode, activeTaskId);
          if (activeSectionTask && isRecoverableTaskInterruption(error, { ...section, activeSectionTask })) {
            setGeneratedSections((prevSections) =>
              prevSections.map((generatedSection) =>
                generatedSection.id === section.id
                  ? {
                      ...generatedSection,
                      activeSectionTask: generatedSection.activeSectionTask || activeSectionTask,
                      chat_async_task_id:
                        activeSectionTask.kind === "chat_async"
                          ? activeSectionTask.taskId
                          : generatedSection.chat_async_task_id,
                      chatAsyncInProgress:
                        activeSectionTask.kind === "chat_async" ? true : generatedSection.chatAsyncInProgress,
                      chatAsyncRegenerationInProgress:
                        activeSectionTask.kind === "chat_async"
                          ? activeSectionTask.mode === "regeneration"
                          : generatedSection.chatAsyncRegenerationInProgress,
                      task_id:
                        activeSectionTask.kind === "agentic" ? activeSectionTask.taskId : generatedSection.task_id,
                      agenticRegenerationInProgress:
                        activeSectionTask.kind === "agentic"
                          ? activeSectionTask.mode === "regeneration"
                          : generatedSection.agenticRegenerationInProgress,
                      agentUpdates: appendTaskUpdateIfNew(generatedSection.agentUpdates || [], {
                        message: "Connection interrupted. Waiting to reconnect...",
                        status: "running",
                        task_id: activeSectionTask.taskId,
                      }),
                    }
                  : generatedSection
              )
            );
            return;
          }

          const readinessNotice = getDocumentsNotReadyPayload(error);
          if (readinessNotice) {
            if (createdGeneratedSectionPlaceholder) {
              setGeneratedSections((prevSections) =>
                prevSections.filter((generatedSection) => generatedSection.id !== section.id)
              );
            }

            setDocumentReadinessNotice(readinessNotice);
            return;
          }

          const responseDetail = error.response?.data?.detail?.trim?.() || error.message?.trim?.() || "";
          const errorMessage = `TODO: Section generation failed (stage: ${generationStage}): An unexpected error occurred during generation. Please try again in a few moments, or please notify Jenly.ai if the issue persists.${
            responseDetail ? ` ${responseDetail}` : ""
          }`;

          Logger.error(`MainPage.tsx: ${errorMessage}`, { sectionId: section.id, sectionName: section.display_name });

          await handleAIOutput({ answer: errorMessage }, section);
        } finally {
          if (uploadedFileInfo) {
            try {
              Logger.debug(`MainPage.tsx: Cleaning up temporary files for section: ${section.display_name}`);

              await deleteDocument(uploadedFileInfo.documentId);

              if (uploadedFileInfo.tags && uploadedFileInfo.tags.length > 0) {
                const tagsToDelete = uploadedFileInfo.tags.filter((tag) => tag.name && tag.name.startsWith("tmp-"));
                await Promise.all(tagsToDelete.map((tag) => deleteTag(tag.id)));
                Logger.debug(`MainPage.tsx: Deleted ${tagsToDelete.length} temporary tags.`);
              }

              Logger.debug("MainPage.tsx: Document deletion and tags cleanup is successful.");
            } catch (cleanupError) {
              Logger.error("MainPage.tsx: Failed to clean up temporary document/tags:", cleanupError);
            }
          }
        }
      };

      if (hasChatCommandActivated === true) {
        const sectionsToGenerateLast = ["executive summary", "abbreviation"];

        const selectableSections = sections.filter(
          (section) => !generatedSections.some((gs) => gs.id === section.id) && sectionStatus[section.id] !== false
        );

        const normalSections = sections
          .filter(
            (s) =>
              !generatedSections.some((gs) => gs.id === s.id) &&
              sectionStatus[s.id] !== false &&
              s.display_name !== "None" &&
              s.display_name !== "Base" &&
              !sectionsToGenerateLast.some((item) => s.display_name.toLowerCase().includes(item))
          )
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));

        Logger.debug("MainPage.tsx: Normal sections count:", normalSections.length);

        const specialSections = selectableSections.filter((s) =>
          sectionsToGenerateLast.some((item) => s.display_name.toLowerCase().includes(item))
        );

        const BATCH_SIZE = 5;

        const processInBatches = async (items) => {
          for (let i = 0; i < items.length; i += BATCH_SIZE) {
            const sectionChunk = items.slice(i, i + BATCH_SIZE);
            setSelectedSections(sectionChunk);
            await Promise.all(sectionChunk.map((section) => generationPromises(section)));
          }
        };

        await processInBatches(normalSections);

        // generating execetive summary sections and Abbreviations list sections at the end
        await processInBatches(specialSections);
      } else {
        // When user triggers generation directly (not via chat command), also process in batches
        const sectionsToGenerateLast = ["executive summary", "abbreviation"];
        const shouldGenerateLast = (section) =>
          sectionsToGenerateLast.some((item) => section.display_name.toLowerCase().includes(item));
        const normalSections = selectedSections.filter((section) => !shouldGenerateLast(section));
        const specialSections = selectedSections.filter((section) => shouldGenerateLast(section));
        const BATCH_SIZE = 5;
        const processInBatches = async (items: any[]) => {
          for (let i = 0; i < items.length; i += BATCH_SIZE) {
            const sectionChunk = items.slice(i, i + BATCH_SIZE);
            setSelectedSections(sectionChunk);
            await Promise.all(sectionChunk.map((section) => generationPromises(section)));
          }
        };

        await processInBatches(normalSections);
        await processInBatches(specialSections);
      }

      setSelectAllSections(false);
      setSelectedSections([]);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setSectionGeneration(false);
      scrollToLastGenerated();
    } catch (error) {
      Logger.error("MainPage.tsx: Error during section generation:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : error.name === "RichApi.Error"
            ? {
                errorLocation: error.debugInfo?.errorLocation,
                statement: error.debugInfo?.statement,
                surroundingStatements: error.debugInfo?.surroundingStatements,
              }
            : undefined,
        },
      });
    }
  };

  return (
    <>
      <Tabs
        tabs={tabs}
        setTabs={setTabs}
        activeTabId={activeTabId}
        setActiveTabId={setActiveTabId}
        showNewTabButton={false}
      />
      <div className="chrome-tab-content-area">
        <div style={{ display: activeTabId === "home" ? "block" : "none", height: "100%" }}>
          <SectionPage
            insertionLockRef={insertionLockRef}
            trackChanges={trackChanges}
            handleTrackChangesToggle={handleTrackChangesToggle}
            // insertTextAtSection={insertTextAtSection}
            insertAtSection={insertAtSection}
            scrollToSection={scrollToSection}
            // createTable={createTable}
            getSelectedText={getSelectedText}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            generatedSections={generatedSections}
            setGeneratedSections={setGeneratedSections}
            sectionStatus={sectionStatus}
            sections={sections}
            templateTagsMap={templateTagsMap}
            selectedProject={selectedProject}
            selectedReportTemplate={selectedReportTemplate}
            sectionEvalStatus={sectionEvalStatus}
            setSectionEvalStatus={setSectionEvalStatus}
            runTask={runTask}
            selectAllSections={selectAllSections}
            setSelectAllSections={setSelectAllSections}
            acceptChanges={acceptChanges}
            declineChanges={declineChanges}
            outputDecision={outputDecision}
            setOutputDecision={setOutputDecision}
            sectionDialogContent={sectionDialogContent}
            setSectionDialogContent={setSectionDialogContent}
            sourceDialogOpen={sourceDialogOpen}
            setSourceDialogOpen={setSourceDialogOpen}
            sourceDialogContent={sourceDialogContent}
            setSourceDialogContent={setSourceDialogContent}
            feedbackDialogOpen={feedbackDialogOpen}
            setFeedbackDialogOpen={setFeedbackDialogOpen}
            feedbackDialogContent={feedbackDialogContent}
            setFeedbackDialogContent={setFeedbackDialogContent}
            regenerateDialogOpen={regenerateDialogOpen}
            setRegenerateDialogOpen={setRegenerateDialogOpen}
            regenerateDialogContent={regenerateDialogContent}
            setRegenerateDialogContent={setRegenerateDialogContent}
            toggleRegenerateOptions={toggleRegenerateOptions}
            regenerateLoading={regenerateLoading}
            setRegenerateLoading={setRegenerateLoading}
            qualityCheckDialogOpen={qualityCheckDialogOpen}
            setQualityCheckDialogOpen={setQualityCheckDialogOpen}
            qualityCheckDialogContent={qualityCheckDialogContent}
            setQualityCheckDialogContent={setQualityCheckDialogContent}
            //formatDateToLongForm={formatDateToLongForm}
            bookmarks={bookmarks}
            projectDetailsExpanded={projectDetailsExpanded}
            setProjectDetailsExpanded={setProjectDetailsExpanded}
            projectPlaceholderDetails={projectPlaceholderDetails}
            messageChatId={messageChatId}
            selectedText={selectedText}
            retrievellmOuputWOGroupAsync={retrievellmOuputWOGroupAsync}
            retrieveSourceDocument={retrieveSourceDocument}
            uploadedFiles={uploadedFiles}
            retreiveSourceDocumentMetadata={retreiveSourceDocumentMetadata}
            uploadDocument={uploadDocument}
            deleteDocument={deleteDocument}
            deleteTag={deleteTag}
            setShowError={setShowError}
            titleMap={titleMap}
            setTest={setTest}
            setActiveTabId={setActiveTabId}
            location={location}
            uploadedAgentFiles={uploadedAgentFiles}
            setUploadedAgentFiles={setUploadedAgentFiles}
            transcribeAudio={transcribeAudio}
            retrieveAgentQuery={retrieveAgentQuery}
            retrieveAgentStatus={retrieveAgentStatus}
            retrieveChatHistory={retrieveChatHistory}
            retrieveChatAsyncTask={retrieveChatAsyncTask}
            waitForChatAsyncResult={waitForChatAsyncResult}
            sectionGeneration={sectionGeneration}
            setSectionGeneration={setSectionGeneration}
            handleAgentStream={handleAgentStream}
            agenticRegenerationRequest={agenticRegenerationRequest}
            handleAIOutput={handleAIOutput}
            handleSectionGeneration={handleSectionGeneration}
            setIsAgentSectionRegenerationOn={setIsAgentSectionRegenerationOn}
            setAgenticRegenerationRequest={setAgenticRegenerationRequest}
            hasSectionBookmarks={hasSectionBookmarks}
            parseMarkdownSections={parseMarkdownSections}
            retrieveDocumentsList={retrieveDocumentsList}
            retrieveDocumentsForImageInsertion={retrieveDocumentsForImageInsertion}
            retrieveDocumentImages={retrieveDocumentImages}
            downloadDocumentImage={downloadDocumentImage}
            submitLangfuseFeedback={submitLangfuseFeedback}
            handleCurrentDocxUpload={handleCurrentDocxUpload}
            setSectionStatus={setSectionStatus}
            clearGroundingForSection={clearGroundingForSection}
            tryClaimAgenticRegenerationRequest={tryClaimAgenticRegenerationRequest}
            releaseAgenticRegenerationRequest={releaseAgenticRegenerationRequest}
            logAuditAction={logAuditAction}
            reportTemplateProjectId={reportTemplateProjectId}
            groundingByTaskId={groundingByTaskId}
            onGraphViewControllerChange={setGraphViewController}
            groundingLoadingTaskIds={groundingLoadingTaskIds}
            groundingErrorsByTaskId={groundingErrorsByTaskId}
            fetchGroundingArtifact={fetchGroundingArtifact}
            documentReadinessNotice={documentReadinessNotice}
            setDocumentReadinessNotice={setDocumentReadinessNotice}
            clearDocumentReadinessNotice={clearDocumentReadinessNotice}
          />
        </div>
        <div style={{ display: activeTabId === "chat" ? "block" : "none", height: "100%" }}>
          <ChatPage
            active={activeTabId === "chat"}
            onSendMessage={handleSendMessage}
            onNewChat={handleNewChat}
            messages={messages}
            setMessages={setMessages}
            messageChatId={messageChatId}
            setMessageChatId={setMessageChatId}
            selectedProjectId={selectedProject?.id}
            selectedReportTemplateId={selectedReportTemplateId}
            location={location}
            transcribeAudio={transcribeAudio}
            synthesizeSpeech={synthesizeSpeech}
            fetchSettingsGroup={fetchSettingsGroup}
            reportTemplateId={reportTemplateId}
            handleSectionGeneration={handleSectionGeneration}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            generatedSections={generatedSections}
            logAuditAction={logAuditAction}
            reportTemplateProjectId={reportTemplateProjectId}
            getProjectChatHistory={getProjectChatHistory}
            getChatThread={getChatThread}
            setChatPinned={setChatPinned}
            renameChatHistory={renameChatHistory}
            deleteChatHistory={deleteChatHistory}
            startAgentQuery={startAgentQuery}
            getAgentTaskStatus={getAgentTaskStatus}
            streamAgentTaskStatus={streamAgentTaskStatus}
            getModels={getModels}
            getProjectTags={getProjectTags}
            getPromptTemplates={getPromptTemplates}
            retrieveAgentGrounding={retrieveAgentGrounding}
            retrieveSourceDocument={retrieveSourceDocument}
          />
        </div>
        <div style={{ display: activeTabId === "references" ? "block" : "none", height: "100%" }}>
          <ReferencePage
            groundingByTaskId={groundingByTaskId}
            loadingTaskIds={groundingLoadingTaskIds}
            errorsByTaskId={groundingErrorsByTaskId}
            sectionOptions={referenceSectionOptions}
            activeTarget={activeGroundingTarget}
            selectedProject={selectedProject}
            reportTemplateId={reportTemplateId}
            draftStyles={draftStyles}
            retrieveSourceDocument={retrieveSourceDocument}
            onReferenceNavigate={scrollToGroundingReference}
            graphView={graphViewController}
          />
        </div>
        {activeTabId.includes("tab-") && <>{tabs.find((tab) => tab.id === activeTabId).content}</>}
      </div>
    </>
  );
};

export default MainPage;
